import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  SidePanel,
  SidePanelType,
  SidePanelLoader,
  ButtonPrimary,
  ButtonPseudo,
  InputText,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetSicCodeQuery,
  useUpdateSicCodeRiskScoreMutation,
} from '../store/services';
import ROUTES from '../../../routes';

import cn from './styles.module.css';

const SicCodePanel = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [riskScoreValue, setRiskScoreValue] = useState('');
  const [riskCategoryValue, setRiskCategoryValue] = useState('');

  const navigate = useNavigate();

  const { id } = useParams();

  const {
    corporateClientsRisk: {
      sicCodes: { sicCode },
    },
    amlDictionaries: { riskScoreRanges },
  } = useAppSelector((state) => state);

  const [getSicCode, { isLoading, isFetching }] = useLazyGetSicCodeQuery();
  const [updateRiskScore, { isLoading: requestInProgress }] =
    useUpdateSicCodeRiskScoreMutation();

  const onSubmit = () => {
    updateRiskScore({
      id: id as string,
      body: {
        riskScore: riskScoreValue ? Number(riskScoreValue) : 0,
      },
    });
  };

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(ROUTES.CORPORATE_CLIENTS_RISK_SIC_CODES);
    }, 100);
  };

  useEffect(() => {
    if (sicCode) {
      setRiskScoreValue(sicCode.riskScore.toString());

      const riskCategory = riskScoreRanges?.find(
        (riskScore) =>
          riskScore?.from <= sicCode.riskScore &&
          riskScore.to >= sicCode.riskScore,
      );

      setRiskCategoryValue(
        `${riskCategory?.name?.charAt(0)}${riskCategory?.name
          .slice(1)
          .toLowerCase()} risk`,
      );
    }
  }, [sicCode]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (id) {
      getSicCode(id);
    }
  }, [id]);

  let content = <div />;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (!isLoading && !isFetching && sicCode) {
    content = (
      <>
        <div className={cn.formGroup}>
          <InputText
            name="code"
            value={sicCode?.code}
            title="Code"
            placeholder=""
            disabled
          />
        </div>
        <div className={cn.formGroup}>
          <InputText
            name="description"
            title="Description"
            placeholder=""
            value={sicCode?.description}
            disabled
          />
        </div>
        <div className={cn.formGroup}>
          <InputText
            name="riskScore"
            title="Risk Score"
            placeholder=""
            value={riskScoreValue}
            onChange={(e) => {
              setRiskScoreValue(e.target.value);

              const riskCategory = riskScoreRanges?.find(
                (riskScore) =>
                  riskScore?.from <= Number(e.target.value) &&
                  riskScore.to >= Number(e.target.value),
              );

              setRiskCategoryValue(
                `${riskCategory?.name?.charAt(0)}${riskCategory?.name
                  .slice(1)
                  .toLowerCase()} risk`,
              );
            }}
          />
        </div>
        <div className={cn.formGroup}>
          <InputText
            name="riskCategory"
            title="Risk Category"
            placeholder=""
            value={riskCategoryValue}
            disabled
          />
        </div>
      </>
    );
  }

  return (
    <SidePanel
      title="SIC Code Details"
      type={SidePanelType.AML}
      isOpen={panelIsOpen}
      onClose={handleClose}
      sidepanelExpandedKey="sic-codes"
      footer={
        <>
          <ButtonPrimary
            text={requestInProgress ? 'Loading...' : 'Save'}
            disabled={
              Boolean(Number(riskScoreValue) === sicCode?.riskScore) ||
              requestInProgress
            }
            onClick={onSubmit}
            bold
          />
          <ButtonPseudo text="Close" onClick={handleClose} bold />
        </>
      }
      closeOnClickOutside
    >
      {content}
    </SidePanel>
  );
};

export default SicCodePanel;
